'use client'

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Check } from 'lucide-react'
import { copyToClipboard } from '@/lib/clipboard'

const COPIED_STATE_TIMEOUT_MS = 2000

interface InAppBrowserNoticeProps {
  browserType: string | null;
}

const BROWSER_NAME_MAPPING: Record<string, string> = {
  'barcelona': 'Threads',
  'threadsapp': 'Threads',
  'threadsandroid': 'Threads',
  'fban': 'Facebook',
  'fbav': 'Facebook',
  'fb_iab': 'Facebook',
  'fb4a': 'Facebook',
  'fbios': 'Facebook',
  'bytedancewebview': 'TikTok',
  'musical_ly': 'TikTok',
  'twitter for': 'X (Twitter)',
  'twitterandroid': 'X (Twitter)',
  'line/': 'Line',
  'kakaotalk': 'KakaoTalk',
  'linkedinapp': 'LinkedIn',
  'instagram': 'Instagram',
  'snapchat': 'Snapchat',
  'debug_override': 'test browser',
};

function getFriendlyBrowserName(browserType: string | null): string {
  if (!browserType) return 'in-app browser';
  const type = browserType.toLowerCase();
  return BROWSER_NAME_MAPPING[type] || browserType;
}

export function InAppBrowserNotice({ browserType }: InAppBrowserNoticeProps) {
  const { t } = useTranslation();
  const browserName = getFriendlyBrowserName(browserType);
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopyUrl = async () => {
    await copyToClipboard(window.location.href);
    setCopied(true);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setCopied(false), COPIED_STATE_TIMEOUT_MS);
  };

  return (
    <p className="pt-3 text-center text-sm leading-relaxed text-white">
      {t('auth.in_app_browser_notice_prefix', 'If you want to use Google sign-in, reopen')}{' '}
      <button
        type="button"
        onClick={handleCopyUrl}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-highlight/20 hover:bg-highlight/30 text-highlight rounded-full border border-highlight/30 transition-colors align-middle"
      >
        {copied ? (
          <>
            <Check className="w-3 h-3" />
            <span className="text-xs font-medium">{t('auth.copied', 'Copied!')}</span>
          </>
        ) : (
          <>
            <Copy className="w-3 h-3" />
            <span className="text-xs font-medium">{window.location.host}</span>
          </>
        )}
      </button>{' '}
      {t(
        'auth.in_app_browser_notice_suffix',
        'in your preferred browser (Chrome / Firefox / Safari) outside of {{browserName}}',
        { browserName }
      )}
    </p>
  );
}
